import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import {
	type SQL,
	and,
	asc,
	desc,
	eq,
	exists,
	gt,
	gte,
	inArray,
	isNull,
	lt,
	lte,
	not,
	notInArray,
	or,
	sql,
} from "drizzle-orm";
import { unionAll } from "drizzle-orm/mysql-core";
import type { MySql2Database } from "drizzle-orm/mysql2";
import { z } from "zod";

import { dateTimeToISO } from "../utils";

import {
	fees as feesTable,
	quoteTransactions as quoteTransactionsTable,
	quotes as quotesTable,
} from "../drizzle/schema";
import { chainsEnum } from "../chains";
import {
	defaultStatuses,
	statusVisibleToUserEnum,
	statusesVisibleToUserArray,
	statusesVisibleToUserToStatuses,
	statusesWithHiddenAmounts,
	shouldHideAmountsForStatus,
} from "../status";
import { type Tokens, getTokenIdsByChain } from "../tokens";
import { URLSearchParamsToStr, numToStr } from "../type-safe-stringify";

import type { AnyInputRecipientsResult } from "./api/getAnyInputRecipients";

extendZodWithOpenApi(z);

/** Zod transform: split an optional comma-separated string into a trimmed array. */
export function csvToArray(val: string | undefined): string[] | undefined {
	return val === undefined
		? undefined
		: val
				.split(",")
				.map((s) => {
					return s.trim();
				})
				.filter(Boolean);
}

/** Zod transform: coerce a single value into a one-element array; pass arrays through. */
export function toStringArray<T extends string>(val: T | T[]): T[] {
	return Array.isArray(val) ? val : [val];
}

function tokenIdsForChains(
	chainIds: readonly string[],
	tokenIdsByChain: Record<string, string[]>,
): string[] {
	return chainIds.flatMap((c) => {
		return Object.hasOwn(tokenIdsByChain, c) ? (tokenIdsByChain[c] ?? []) : [];
	});
}

export function mergeChainAndTokenIds(
	chainIds: readonly string[] | undefined,
	tokenIds: readonly string[] | undefined,
	tokens: Tokens,
): string[] | undefined {
	const hasInput = (chainIds?.length ?? 0) > 0 || (tokenIds?.length ?? 0) > 0;

	if (!hasInput) {
		return undefined;
	}

	// any unknown chain poisons the filter — caller short-circuits to zero rows
	const tokenIdsByChain =
		chainIds === undefined ? {} : getTokenIdsByChain(tokens);

	if (chainIds !== undefined) {
		for (const c of chainIds) {
			if (!Object.hasOwn(tokenIdsByChain, c)) {
				return [];
			}
		}
	}

	return [
		...(chainIds === undefined
			? []
			: tokenIdsForChains(chainIds, tokenIdsByChain)),
		...(tokenIds ?? []),
	];
}

/** Zero stands in for an amount we are not willing to publish yet. */
const HIDDEN_AMOUNT = "0";

/**
 * Picks the value to publish for one amount: the settled figure, the quote as
 * a fallback, or zero while the swap is still in flight.
 */
function amountPublisher(status: string, hasNearSwapTransaction: boolean) {
	const withheld = shouldHideAmountsForStatus(status, hasNearSwapTransaction);

	return (settled: string | null, quoted: string) => {
		return withheld ? HIDDEN_AMOUNT : (settled ?? quoted);
	};
}

/**
 * The gate every amount passes through before it leaves the server.
 *
 * Every amount is stored twice. `amount*` is the quote the user was given up
 * front, `actual*` is what the swap paid out. They differ by the realised
 * slippage, so publish `actual*` and fall back to the quote only when it is
 * missing. 1Click does the same:
 * https://github.com/chaindefuser/monorepos/blob/09be414178a15fc60f31672befe0c4afde49fd83/1click/1click-manager/src/modules/history/history.service.ts#L222-L225
 * https://github.com/chaindefuser/monorepos/blob/09be414178a15fc60f31672befe0c4afde49fd83/1click/1click-manager/src/modules/processor/processor.service.ts#L394-L399
 *
 * A swap still in flight has no `actual*` yet, and the quote must not stand in
 * for it. The size of an unfinished swap is enough to trade ahead of it, so
 * those rows report zero.
 */
export function withSettledAmounts<T extends SettledAmountColumns>(
	{
		actualAmountInFormatted,
		actualAmountInUsd,
		actualAmountOutFormatted,
		actualAmountOutUsd,
		amountInFormatted,
		amountInUsd,
		amountOutFormatted,
		amountOutUsd,
		...rest
	}: T,
	{ hasNearSwapTransaction }: { hasNearSwapTransaction: boolean },
) {
	const publish = amountPublisher(rest.status, hasNearSwapTransaction);

	return {
		...rest,
		amountInFormatted: publish(actualAmountInFormatted, amountInFormatted),
		amountInUsd: publish(actualAmountInUsd, amountInUsd),
		amountOutFormatted: publish(actualAmountOutFormatted, amountOutFormatted),
		amountOutUsd: publish(actualAmountOutUsd, amountOutUsd),
	};
}

/** {@link withSettledAmounts} plus the base-unit amounts the API exposes. */
export function withSettledBaseUnitAmounts<
	T extends SettledAmountColumns & SettledBaseUnitAmountColumns,
>(
	{ actualAmountIn, actualAmountOut, amountIn, amountOut, ...rest }: T,
	options: { hasNearSwapTransaction: boolean },
) {
	const publish = amountPublisher(rest.status, options.hasNearSwapTransaction);

	return {
		...withSettledAmounts(rest, options),
		amountIn: publish(actualAmountIn, amountIn),
		amountOut: publish(actualAmountOut, amountOut),
	};
}

type SettledAmountColumns = {
	status: string;
	actualAmountInFormatted: string | null;
	actualAmountInUsd: string | null;
	actualAmountOutFormatted: string | null;
	actualAmountOutUsd: string | null;
	amountInFormatted: string;
	amountInUsd: string;
	amountOutFormatted: string;
	amountOutUsd: string;
};

type SettledBaseUnitAmountColumns = {
	actualAmountIn: string | null;
	actualAmountOut: string | null;
	amountIn: string;
	amountOut: string;
};

export const DEFAULT_NUMBER_OF_TRANSACTIONS = 50;

const DEFAULT_NUMBER_OF_TRANSACTIONS_STR = numToStr(
	DEFAULT_NUMBER_OF_TRANSACTIONS,
);

export const MAX_NUMBER_OF_TRANSACTIONS = 1000;

const MAX_NUMBER_OF_TRANSACTIONS_STR = numToStr(MAX_NUMBER_OF_TRANSACTIONS);

const getTransactionsSearchParamsSchemaCommon = z.object({
	search: z
		.string()
		.optional()
		.describe("Search by deposit address, recipient, sender, or tx hash."),
	fromChainId: z
		.union([chainsEnum, z.array(chainsEnum)])
		.transform(toStringArray)
		.optional()
		.describe(
			"Filter by origin chain IDs. Repeat the parameter to pass multiple ids (matched as OR). Swap-specific: setting this excludes masking transactions.",
		),
	fromTokenId: z
		.union([z.string(), z.array(z.string())])
		.transform(toStringArray)
		.optional()
		.describe(
			"Filter by origin token IDs. Repeat the parameter to pass multiple ids (matched as OR). Overrides fromChainId. Swap-specific: setting this excludes masking transactions.",
		),
	toChainId: z
		.union([chainsEnum, z.array(chainsEnum)])
		.transform(toStringArray)
		.optional()
		.describe(
			"Filter by destination chain IDs. Repeat the parameter to pass multiple ids (matched as OR). Swap-specific: setting this excludes masking transactions.",
		),
	toTokenId: z
		.union([z.string(), z.array(z.string())])
		.transform(toStringArray)
		.optional()
		.describe(
			"Filter by destination token IDs. Repeat the parameter to pass multiple ids (matched as OR). Overrides toChainId. Swap-specific: setting this excludes masking transactions.",
		),
	depositMemo: z
		.string()
		.optional()
		.describe("Deposit memo to narrow search by deposit address."),
	referral: z
		.string()
		.optional()
		.describe(
			"Filter by referral. Swap-specific: setting this excludes masking transactions.",
		),
	affiliate: z
		.string()
		.optional()
		.describe(
			"Filter by an affiliate stored in the fees. Swap-specific: setting this excludes masking transactions.",
		),
	statuses: z
		.string()
		.optional()
		.describe(
			`Filter by comma-separated statuses. Allowed values: ${statusesVisibleToUserArray.join(
				", ",
			)}. Default: ${defaultStatuses.join(", ")}.`,
		),
	showTestTxs: z
		.string()
		.optional()
		.describe(
			"Show transactions from test accounts and with test-related referrals.",
		),
	minUsdPrice: z.coerce
		.number()
		.min(0)
		.optional()
		.describe(
			"Filter by minimum USD price for both input and output amounts. Swap-specific: setting this excludes masking transactions.",
		),
	maxUsdPrice: z.coerce
		.number()
		.min(0)
		.optional()
		.describe(
			"Filter by maximum USD price for both input and output amounts. Swap-specific: setting this excludes masking transactions.",
		),
	endTimestamp: z.coerce
		.date()
		.optional()
		.describe("Filter by end timestamp (non-inclusive). Use ISO 8601 format."),
	endTimestampUnix: z.coerce
		.number()
		.optional()
		.describe("Filter by end timestamp (non-inclusive). Use Unix timestamp."),
	startTimestamp: z.coerce
		.date()
		.optional()
		.describe(
			"Filter by start timestamp (non-inclusive). Use ISO 8601 format.",
		),
	startTimestampUnix: z.coerce
		.number()
		.optional()
		.describe("Filter by start timestamp (non-inclusive). Use Unix timestamp."),
});

export const getTransactionsSearchParamsSchemaCursor = z
	.object({
		numberOfTransactions: z.coerce
			.number()
			.min(1)
			.max(MAX_NUMBER_OF_TRANSACTIONS)
			.optional()
			.describe(
				`The number of transactions to return. Default: ${DEFAULT_NUMBER_OF_TRANSACTIONS_STR}. Max: ${MAX_NUMBER_OF_TRANSACTIONS_STR}. Min: 1.`,
			),
		lastDepositAddressAndMemo: z
			.string()
			.optional()
			.describe(
				"DEPRECATED: Use lastDepositAddress and lastDepositMemo instead. Combined deposit address and memo value for pagination.",
			),
		lastDepositAddress: z
			.string()
			.optional()
			.describe(
				"Last known deposit address for pagination. Use the depositAddress from the last transaction of the previous page.",
			),
		lastDepositMemo: z
			.string()
			.optional()
			.describe(
				"Last known deposit memo for pagination. Use the depositMemo from the last transaction of the previous page.",
			),
		direction: z
			.enum(["next", "prev"])
			.optional()
			.describe(
				"Direction of pagination. `next` - get older transactions, `prev` - get newer transactions. Default: `next`.",
			),
	})
	.extend(getTransactionsSearchParamsSchemaCommon.shape)
	.openapi("GetTransactionsSearchParamsCursor", {
		param: { name: "GetTransactionsSearchParamsCursor" },
	});

export type GetTransactionsSearchParamsCursor = z.infer<
	typeof getTransactionsSearchParamsSchemaCursor
>;

// Base schema without transform for internal use and extending
const getTransactionsParsedSearchParamsSchemaCursorBase =
	getTransactionsSearchParamsSchemaCursor.extend({
		showTestTxs: z
			.string()
			.optional()
			.transform((v) => {
				return v !== undefined;
			}),
		statuses: z
			.string()
			.optional()
			.transform(csvToArray)
			.pipe(
				z
					.array(statusVisibleToUserEnum)
					.or(z.undefined())
					.transform((v) => {
						return v === undefined ? undefined : v.sort();
					}),
			),
		numberOfTransactions: z.coerce
			.number()
			.min(1)
			.max(MAX_NUMBER_OF_TRANSACTIONS)
			.default(DEFAULT_NUMBER_OF_TRANSACTIONS),
		direction: z.enum(["next", "prev"]).default("next"),
		lastCreatedAt: z.string().optional(),
	});

// API schema - prefers lastDepositAddress+lastDepositMemo, falls back to deprecated lastDepositAddressAndMemo
export const getTransactionsParsedSearchParamsSchemaCursorApi =
	getTransactionsParsedSearchParamsSchemaCursorBase.transform(
		({
			lastDepositAddressAndMemo,
			lastDepositAddress,
			lastDepositMemo,
			...rest
		}) => {
			// Prefer separate fields over deprecated combined field
			if (lastDepositAddress !== undefined) {
				return {
					...rest,
					lastDepositAddress,
					lastDepositMemo: lastDepositMemo ?? undefined,
				};
			}

			// Backward compat: parse deprecated lastDepositAddressAndMemo
			if (lastDepositAddressAndMemo !== undefined) {
				const parsed = transactionIdToDepositAddressWithMemo(
					lastDepositAddressAndMemo,
				);

				return {
					...rest,
					lastDepositAddress: parsed.depositAddress,
					lastDepositMemo: parsed.depositMemo ?? undefined,
				};
			}

			return {
				...rest,
				lastDepositAddress: undefined,
				lastDepositMemo: undefined,
			};
		},
	);

export type GetTransactionsParsedSearchParamsCursorApi = z.infer<
	typeof getTransactionsParsedSearchParamsSchemaCursorApi
>;

export const transactionTypeEnum = z.enum(["all", "swaps", "masking"]);

export type TransactionType = z.infer<typeof transactionTypeEnum>;

export const getTransactionsParsedSearchParamsSchemaCursor =
	getTransactionsParsedSearchParamsSchemaCursorBase.extend({
		lastDepositMemo: z.string().optional(),
		lastRelatedReceiptId: z.string().optional(),
		transactionType: transactionTypeEnum.optional().default("swaps"),
		sources: z
			.string()
			.optional()
			.transform(csvToArray)
			.pipe(z.array(z.enum(["swaps", "masking"])).or(z.undefined())),
	});

export type GetTransactionsParsedSearchParamsCursor = z.infer<
	typeof getTransactionsParsedSearchParamsSchemaCursor
>;

export const getTransactionsSearchParamsSchemaPages = z
	.object({
		page: z.coerce
			.number()
			.min(1)
			.optional()
			.describe("Page number. Default: 1. Min: 1."),
		perPage: z.coerce
			.number()
			.min(1)
			.max(MAX_NUMBER_OF_TRANSACTIONS)
			.optional()
			.describe(
				`Number of transactions per page. Default: ${DEFAULT_NUMBER_OF_TRANSACTIONS_STR}. Max: ${MAX_NUMBER_OF_TRANSACTIONS_STR}. Min: 1.`,
			),
	})
	.extend(getTransactionsSearchParamsSchemaCommon.shape)
	.openapi("GetTransactionsSearchParamsPages", {
		param: { name: "GetTransactionsSearchParamsPages" },
	});

export type GetTransactionsSearchParamsPages = z.infer<
	typeof getTransactionsSearchParamsSchemaPages
>;

export const getTransactionsParsedSearchParamsSchemaPages =
	getTransactionsSearchParamsSchemaPages.extend({
		showTestTxs: z
			.string()
			.optional()
			.transform((v) => {
				return v === "true";
			}),
		statuses: z
			.string()
			.optional()
			.transform(csvToArray)
			.pipe(
				z
					.array(statusVisibleToUserEnum)
					.or(z.undefined())
					.transform((v) => {
						return v === undefined ? undefined : v.sort();
					}),
			),
		perPage: z.coerce
			.number()
			.min(1)
			.max(MAX_NUMBER_OF_TRANSACTIONS)
			.default(DEFAULT_NUMBER_OF_TRANSACTIONS),
		page: z.coerce
			.number()
			.min(1)
			.optional()
			.default(1)
			.describe("Page number. Default: 1. Min: 1."),
	});

export type GetTransactionsParsedSearchParamsPages = z.infer<
	typeof getTransactionsParsedSearchParamsSchemaPages
>;

export const testReferrals = [
	"e2e-tests",
	"e2e_tests",
	"one_click_sdk_rs-e2e",
	"test",
	"testing",
	"automated-test",
	"diagnostic-test",
	"comprehensive-test",
	"origin-chain-test",
];

export const testRecipients = [
	"recipient",
	"noselleg.near",
	"0xca59A8A52360aB1590ABd1f4b459616F4c94e2A9",
	"t1bmqQPnVNp8gDZngCegRBgtT6XMdJowkfg",
	"8DQEvwwAZMz6nSaL5a5rVxsUy5294o9kkL3bYEoes7QB",
	"f306ecc48cdccffca102a0ee64a8b0ef8cabdfd34fc9941addd4f62512feeadf",
	"TQdtVpnaNUnRf1oNWoLCwyzYuvHbnYir4p",
	"1AjuwuFFEBcEqAihGTcrcAGtX7LAXngPEC",
	"DBu73nutYQf9aqDRM9v1xnfcC4QzTGPePx",
	"rn3ojw154X3DPEihAr4VE4Wseh73ZDtLWo",
	"0x1721fbb44FdDbDBd0643d76dD5b0eBB83d0815f6",
	"6Ay24fENb5SZ1oAcQvkYvJ6wSh9YAtGWeL2PFyezKzaD",
	"defuse-es.near",
];

// Senders
const sendersSchema = z.array(z.string().or(z.null()));

export const aggregatedSenders = sql<string>`
COALESCE(
	(
		SELECT JSON_ARRAYAGG(${quoteTransactionsTable.sender})
		FROM ${quoteTransactionsTable}
		WHERE ${quoteTransactionsTable.quoteId} = ${quotesTable.id}
		AND ${quoteTransactionsTable.transactionType} = 'DEPOSIT'
	),
	'[]'
)
`.as("senders");

const hasNearSwapTransactionExpr = sql<number>`
(
	SELECT COUNT(*) > 0
	FROM ${quoteTransactionsTable}
	WHERE ${quoteTransactionsTable.quoteId} = ${quotesTable.id}
	AND ${quoteTransactionsTable.transactionType} = 'NEAR_SWAP'
)
`;

export const hasNearSwapTransaction = hasNearSwapTransactionExpr.as(
	"hasNearSwapTransaction",
);

export function parseSenders(sendersJson: string): string[] {
	return Array.from(
		new Set(
			sendersSchema
				.parse(JSON.parse(sendersJson))
				.filter(Boolean)
				.flatMap((s) => {
					return s.split(",").filter(Boolean);
				}),
		),
	);
}

// Affiliates
const affiliatesSchema = z.array(z.string().or(z.null()));

export const aggregatedAffiliates =
	sql<string>`COALESCE(JSON_ARRAYAGG(CASE WHEN ${feesTable.id} IS NOT NULL THEN ${feesTable.recipient} END), '[]')`.as(
		"affiliates",
	);

export function parseAffiliates(
	affiliatesJson: string,
	recipientToActualRecipient: Record<string, string>,
): string[] {
	return Array.from(
		new Set(
			affiliatesSchema
				.parse(JSON.parse(affiliatesJson))
				.filter((a): a is string => {
					return a !== null;
				})
				.map((a) => {
					return recipientToActualRecipient[a] ?? a;
				}),
		),
	);
}

// App fees
const appFeesSchema = z.array(
	z.object({ recipient: z.string(), fee: z.number() }).or(z.null()),
);

export const aggregatedAppFees =
	sql<string>`COALESCE(JSON_ARRAYAGG(CASE WHEN ${feesTable.id} IS NOT NULL THEN JSON_OBJECT('recipient', ${feesTable.recipient}, 'fee', ${feesTable.fee}) END), '[]')`.as(
		"appFees",
	);

export function parseAppFees(
	appFeesJson: string,
	recipientToActualRecipient: Record<string, string>,
): Array<{ recipient: string; fee: number }> {
	return appFeesSchema
		.parse(JSON.parse(appFeesJson))
		.filter(Boolean)
		.map((appFee) => {
			return {
				...appFee,
				recipient:
					recipientToActualRecipient[appFee.recipient] ?? appFee.recipient,
			};
		});
}

const STELLAR_DEPOSIT_ADDRESSES = [
	"GDJ4JZXZELZD737NVFORH4PSSQDWFDZTKW3AIDKHYQG23ZXBPDGGQBJK",
];

export function transactionIdToDepositAddressWithMemo(id: string) {
	const isStellarAddress = STELLAR_DEPOSIT_ADDRESSES.some((stellarAddr) => {
		return id.startsWith(stellarAddr);
	});

	if (!isStellarAddress) {
		return { depositAddress: id, depositMemo: null };
	}

	const parts = id.split("_");
	const [depositAddress, depositMemo, ...rest] = parts;

	if (
		depositAddress === undefined ||
		depositMemo === undefined ||
		rest.length > 0 ||
		!STELLAR_DEPOSIT_ADDRESSES.includes(depositAddress) ||
		depositMemo.trim() === ""
	) {
		return { depositAddress: id, depositMemo: null };
	}

	return { depositAddress, depositMemo: depositMemo.trim() };
}

export function depositAddressWithMemoToTransactionId({
	depositAddress,
	depositMemo,
}: {
	depositAddress: string;
	depositMemo: string | null | undefined;
}) {
	return depositMemo === undefined ||
		depositMemo === null ||
		!STELLAR_DEPOSIT_ADDRESSES.includes(depositAddress)
		? depositAddress
		: `${depositAddress}_${depositMemo}`;
}

export function getTransactionCacheKey(tx: {
	depositAddress: string;
	depositMemo?: string | null | undefined;
}): string {
	return JSON.stringify([tx.depositAddress, tx.depositMemo ?? null]);
}

/**
 * Get base conditions for filtering transactions.
 *
 * @param params - The parsed search parameters
 * @param db - Database instance
 * @param anyInputRecipients - Any input recipients data for affiliate filtering
 */
/**
 * SQL counterpart of {@link withSettledAmounts}, so filters read the same
 * number the response reports. Comparing against the real value would let a
 * caller bisect `minUsdPrice` and recover the size of a withheld swap.
 */
const settledAmountInUsd = sql`CASE WHEN ${inArray(quotesTable.status, [
	...statusesWithHiddenAmounts,
])} AND NOT ${hasNearSwapTransactionExpr} THEN 0 ELSE COALESCE(${quotesTable.actualAmountInUsd}, ${quotesTable.amountInUsd}) END`;

export function getBaseConditions(
	{
		search,
		depositMemo,
		fromChainId,
		toChainId,
		statuses: statusesFromParams,
		showTestTxs,
		referral,
		minUsdPrice,
		maxUsdPrice,
		startTimestamp,
		startTimestampUnix,
		endTimestamp,
		endTimestampUnix,
		fromTokenId,
		toTokenId,
		affiliate,
		affiliateQuoteIds = {},
	}: Omit<
		GetTransactionsParsedSearchParamsCursorApi,
		| "numberOfTransactions"
		| "direction"
		| "lastCreatedAt"
		| "lastDepositAddress"
		| "lastDepositMemo"
	> & { affiliateQuoteIds?: AffiliateQuoteIdRange },
	db: MySql2Database,
	anyInputRecipients: AnyInputRecipientsResult,
	tokens: Tokens,
) {
	const tokenIdsByChain =
		fromChainId === undefined && toChainId === undefined
			? {}
			: getTokenIdsByChain(tokens);

	const fromChainTokens =
		fromChainId === undefined
			? []
			: tokenIdsForChains(fromChainId, tokenIdsByChain);

	const toChainTokens =
		toChainId === undefined
			? []
			: tokenIdsForChains(toChainId, tokenIdsByChain);

	const baseConditions = [
		inArray(
			quotesTable.status,
			statusesVisibleToUserToStatuses(statusesFromParams),
		),
		getSearchConditions(search, depositMemo, db),
		// A chain with no known tokens matches nothing (inArray([]) is false), not every chain.
		fromChainId !== undefined && fromChainId.length > 0
			? inArray(quotesTable.originAsset, fromChainTokens)
			: undefined,
		toChainId !== undefined && toChainId.length > 0
			? inArray(quotesTable.destinationAsset, toChainTokens)
			: undefined,
		fromTokenId !== undefined && fromTokenId.length > 0
			? inArray(quotesTable.originAsset, fromTokenId)
			: undefined,
		toTokenId !== undefined && toTokenId.length > 0
			? inArray(quotesTable.destinationAsset, toTokenId)
			: undefined,
		showTestTxs
			? undefined
			: and(
					or(
						isNull(quotesTable.referral),
						notInArray(quotesTable.referral, testReferrals),
					),
					notInArray(quotesTable.recipient, testRecipients),
				),
		referral !== undefined ? eq(quotesTable.referral, referral) : undefined,
		minUsdPrice !== undefined
			? gte(sql`CAST(${settledAmountInUsd} AS DECIMAL(20,8))`, minUsdPrice)
			: undefined,
		maxUsdPrice !== undefined
			? lte(sql`CAST(${settledAmountInUsd} AS DECIMAL(20,8))`, maxUsdPrice)
			: undefined,
		startTimestamp !== undefined
			? gt(quotesTable.createdAt, startTimestamp.toISOString())
			: undefined,
		startTimestampUnix !== undefined
			? gt(
					quotesTable.createdAt,
					new Date(startTimestampUnix * 1000).toISOString(),
				)
			: undefined,
		endTimestamp !== undefined
			? lt(quotesTable.createdAt, endTimestamp.toISOString())
			: undefined,
		endTimestampUnix !== undefined
			? lt(
					quotesTable.createdAt,
					new Date(endTimestampUnix * 1000).toISOString(),
				)
			: undefined,
	].filter(Boolean);

	if (affiliate !== undefined) {
		baseConditions.push(
			exists(
				db
					.select({ value: sql`1` })
					.from(feesTable)
					.where(
						and(
							eq(feesTable.quoteId, quotesTable.id),
							getAffiliateCondition(
								affiliate,
								anyInputRecipients.actualRecipientToRecipients,
							),
							affiliateQuoteIds.above === undefined
								? undefined
								: gt(feesTable.quoteId, affiliateQuoteIds.above),
							affiliateQuoteIds.below === undefined
								? undefined
								: lt(feesTable.quoteId, affiliateQuoteIds.below),
						),
					),
			),
		);
	}

	return baseConditions;
}

/**
 * Returns a SQL condition to filter quotes by search term.
 * Uses UNION ALL to find matching quote IDs from multiple sources:
 * - Quotes.depositAddress (with optional depositMemo for quotes with depositMemo)
 * - Quotes.recipient
 * - QuoteTransactions.txHash
 * - QuoteTransactions.sender
 *
 * This approach is more efficient than OR + EXISTS as each subquery
 * can independently use its respective index.
 */
function getSearchConditions(
	search: string | undefined,
	depositMemoParam: string | undefined,
	db: MySql2Database,
) {
	if (search === undefined) {
		return undefined;
	}

	// If depositMemoParam is explicitly provided, use it directly instead of parsing from the search string
	const { depositAddress, depositMemo } =
		depositMemoParam !== undefined
			? { depositAddress: search, depositMemo: depositMemoParam }
			: transactionIdToDepositAddressWithMemo(search);

	// Build subquery: find all quoteIds matching the search term using UNION ALL
	// Each select can utilize its own index efficiently

	// 1. Match by depositAddress (and optionally depositMemo for quotes with depositMemo)
	const depositAddressQuery =
		depositMemo === null
			? db
					.select({ quoteId: quotesTable.id })
					.from(quotesTable)
					.where(eq(quotesTable.depositAddress, search))
			: db
					.select({ quoteId: quotesTable.id })
					.from(quotesTable)
					.where(
						and(
							eq(quotesTable.depositAddress, depositAddress),
							eq(quotesTable.depositMemo, depositMemo),
						),
					);

	// 2. Match by recipient
	const recipientQuery = db
		.select({ quoteId: quotesTable.id })
		.from(quotesTable)
		.where(eq(quotesTable.recipient, search));

	// 3. Match by txHash in QuoteTransactions
	const txHashQuery = db
		.select({ quoteId: quoteTransactionsTable.quoteId })
		.from(quoteTransactionsTable)
		.where(eq(quoteTransactionsTable.txHash, search));

	// 4. Match by sender in QuoteTransactions
	const senderQuery = db
		.select({ quoteId: quoteTransactionsTable.quoteId })
		.from(quoteTransactionsTable)
		.where(eq(quoteTransactionsTable.sender, search));

	// Combine all queries with UNION ALL, then get distinct quoteIds. A
	// QuoteTransactions query goes first: its nullable quoteId types the union.
	const matchingQuoteIds = unionAll(
		txHashQuery,
		senderQuery,
		depositAddressQuery,
		recipientQuery,
	).as("matchingQuoteIds");

	// Return condition: quote.id must be in the set of matching quoteIds
	return inArray(
		quotesTable.id,
		db
			.selectDistinct({ quoteId: matchingQuoteIds.quoteId })
			.from(matchingQuoteIds),
	);
}

/**
 * This function is needed because
 * some of the fee recipients (affiliates)
 * want to receive their fees in stable coins using ANY_INPUT quote
 * So in in order to find depositAddresses for these affiliates,
 * we need to use the actual recipient to recipients map.
 */
function getAffiliateCondition(
	affiliate: string,
	actualRecipientToRecipients: Record<string, string[]>,
) {
	const recipients = actualRecipientToRecipients[affiliate] ?? [affiliate];
	const [firstRecipient] = recipients;

	if (firstRecipient === undefined) {
		throw new Error(
			`Unreachable. Got empty array of recipients for affiliate ${affiliate}`,
		);
	}

	return recipients.length === 1
		? eq(feesTable.recipient, firstRecipient)
		: inArray(feesTable.recipient, recipients);
}

export type AffiliateQuoteIdRange = {
	above?: number | undefined;
	below?: number | undefined;
};

/**
 * Narrows the affiliate EXISTS to a quote id window that holds every row the
 * (createdAt, depositAddress) ordered query can return, given {@link idOrderMargin}.
 * Without it MySQL looks up every quote the affiliate ever earned a fee on.
 * Returns null when no quote matches.
 *
 * @param conditions - Every filter except the affiliate, including the cursor
 */
export async function getAffiliateQuoteIdRange({
	db,
	affiliate,
	actualRecipientToRecipients,
	conditions,
	cursorCreatedAt,
	direction,
	limit,
}: {
	db: MySql2Database;
	affiliate: string;
	actualRecipientToRecipients: Record<string, string[]>;
	conditions: (SQL | undefined)[];
	cursorCreatedAt: string | undefined;
	direction: "next" | "prev";
	limit: number;
}): Promise<AffiliateQuoteIdRange | null> {
	if (direction === "prev") {
		return cursorCreatedAt === undefined
			? {}
			: { above: await getLastQuoteIdBefore(db, cursorCreatedAt) };
	}

	const below =
		cursorCreatedAt === undefined
			? undefined
			: await getFirstQuoteIdAfter(db, cursorCreatedAt);

	const recipients = actualRecipientToRecipients[affiliate] ?? [affiliate];

	// One query per recipient: keep the unbounded query for long ANY_INPUT address lists
	if (recipients.length > 10) {
		return { below };
	}

	const quotesById = new Map<number, { id: number; createdAt: string }>();

	// Each recipient's newest fees first, so a few thousand lookups find `limit` matches.
	// One at a time: a transaction's connection runs them in sequence anyway.
	for (const recipient of recipients) {
		const recipientQuotes = await db
			// A quote can have several fees per recipient
			.selectDistinct({
				id: quotesTable.id,
				createdAt: quotesTable.createdAt,
				quoteId: feesTable.quoteId,
			})
			// MySQL otherwise picks a reverse scan over every affiliate's fees
			.from(feesTable, { ignoreIndex: "IDX__fees__quoteId" })
			.innerJoin(quotesTable, eq(quotesTable.id, feesTable.quoteId))
			.where(
				and(
					eq(feesTable.recipient, recipient),
					below === undefined ? undefined : lt(feesTable.quoteId, below),
					...conditions,
				),
			)
			.orderBy(desc(feesTable.quoteId))
			.limit(limit);

		for (const quote of recipientQuotes) {
			quotesById.set(quote.id, quote);
		}
	}

	const rows = [...quotesById.values()].sort((a, b) => {
		return a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
	});

	// `limit` matches at or after this createdAt exist, so the ordered query stops there
	const oldestNeeded = rows[limit - 1];

	if (oldestNeeded !== undefined) {
		return {
			above: await getLastQuoteIdBefore(db, oldestNeeded.createdAt),
			below,
		};
	}

	// Every recipient ran out of fees: these rows are all the matches
	if (rows.length === 0) {
		return null;
	}

	return {
		above:
			Math.min(
				...rows.map(({ id }) => {
					return id;
				}),
			) - 1,
		below,
	};
}

// Temporary perf fix: assumes id and createdAt order differ by under an hour (worst seen: 12 min).
// Proper fix: an index covering affiliate and createdAt.
const idOrderMargin = sql`INTERVAL 1 HOUR`;

/** Every quote created at or after `createdAt` has a higher id. */
async function getLastQuoteIdBefore(db: MySql2Database, createdAt: string) {
	const [quote] = await db
		.select({ id: quotesTable.id })
		.from(quotesTable)
		.where(
			lt(quotesTable.createdAt, sql`DATE_SUB(${createdAt}, ${idOrderMargin})`),
		)
		.orderBy(desc(quotesTable.createdAt))
		.limit(1);

	return quote?.id;
}

/** Every quote created at or before `createdAt` has a lower id. */
async function getFirstQuoteIdAfter(db: MySql2Database, createdAt: string) {
	const [quote] = await db
		.select({ id: quotesTable.id })
		.from(quotesTable)
		.where(
			gt(quotesTable.createdAt, sql`DATE_ADD(${createdAt}, ${idOrderMargin})`),
		)
		.orderBy(asc(quotesTable.createdAt))
		.limit(1);

	return quote?.id;
}

/**
 * This function is for speeding up the query
 * by getting the ids of the quotes first
 * and then using them in the main query
 */
export async function getFetchQuotesWhere(
	db: MySql2Database,
	dataFetchingConditions: (SQL | undefined)[],
	orderBy: SQL[],
	numberOfTransactions: number,
) {
	const ids = await db
		.select({ id: quotesTable.id })
		.from(quotesTable)
		.where(
			dataFetchingConditions.length > 0
				? and(...dataFetchingConditions)
				: undefined,
		)
		.orderBy(...orderBy)
		.limit(numberOfTransactions);

	if (ids.length === 0) {
		return null;
	}

	return sql`${quotesTable.id} IN (${sql.join(
		ids.map(({ id }) => {
			return sql`${id}`;
		}),
		sql`, `,
	)})`;
}

export function getPaginationCondition(
	lastCreatedAt: string | undefined,
	lastDepositAddress: string | undefined,
	lastDepositMemo: string | undefined,
	direction: string,
) {
	if (lastCreatedAt === undefined) {
		return undefined;
	}

	if (direction === "prev") {
		if (lastDepositAddress === undefined) {
			return gt(quotesTable.createdAt, lastCreatedAt);
		}

		if (lastDepositMemo !== undefined) {
			const andCondition = and(
				eq(quotesTable.depositAddress, lastDepositAddress),
				eq(quotesTable.depositMemo, lastDepositMemo),
			);

			return or(
				and(
					gt(quotesTable.createdAt, lastCreatedAt),
					andCondition === undefined ? undefined : not(andCondition),
				),
				and(
					eq(quotesTable.createdAt, lastCreatedAt),
					or(
						gt(quotesTable.depositAddress, lastDepositAddress),
						and(
							eq(quotesTable.depositAddress, lastDepositAddress),
							gt(quotesTable.depositMemo, lastDepositMemo),
						),
					),
				),
			);
		}

		return or(
			and(
				gt(quotesTable.createdAt, lastCreatedAt),
				not(eq(quotesTable.depositAddress, lastDepositAddress)),
			),
			and(
				eq(quotesTable.createdAt, lastCreatedAt),
				gt(quotesTable.depositAddress, lastDepositAddress),
			),
		);
	}

	if (lastDepositAddress === undefined) {
		return lt(quotesTable.createdAt, lastCreatedAt);
	}

	if (lastDepositMemo !== undefined) {
		const andCondition = and(
			eq(quotesTable.depositAddress, lastDepositAddress),
			eq(quotesTable.depositMemo, lastDepositMemo),
		);

		return or(
			and(
				lt(quotesTable.createdAt, lastCreatedAt),
				andCondition === undefined ? undefined : not(andCondition),
			),
			and(
				eq(quotesTable.createdAt, lastCreatedAt),
				or(
					lt(quotesTable.depositAddress, lastDepositAddress),
					and(
						eq(quotesTable.depositAddress, lastDepositAddress),
						lt(quotesTable.depositMemo, lastDepositMemo),
					),
				),
			),
		);
	}

	return or(
		and(
			lt(quotesTable.createdAt, lastCreatedAt),
			not(eq(quotesTable.depositAddress, lastDepositAddress)),
		),
		and(
			eq(quotesTable.createdAt, lastCreatedAt),
			lt(quotesTable.depositAddress, lastDepositAddress),
		),
	);
}

export async function getLastDepositAddressCreatedAt(
	db: MySql2Database,
	lastDepositAddress: string | undefined,
	lastDepositMemo: string | null | undefined,
) {
	return lastDepositAddress !== undefined
		? (
				await db
					.select({ createdAt: dateTimeToISO(quotesTable, "createdAt") })
					.from(quotesTable)
					.where(
						lastDepositMemo !== undefined && lastDepositMemo !== null
							? and(
									eq(quotesTable.depositAddress, lastDepositAddress),
									eq(quotesTable.depositMemo, lastDepositMemo),
								)
							: eq(quotesTable.depositAddress, lastDepositAddress),
					)
					.limit(1)
			)[0]?.createdAt
		: undefined;
}

export function buildTransactionPageHref({
	depositAddress,
	depositMemo,
}: {
	depositAddress: string;
	depositMemo: string | null | undefined;
}): string {
	const params = new URLSearchParams();

	if (depositMemo !== undefined && depositMemo !== null) {
		params.set("depositMemo", depositMemo);
	}

	const query = URLSearchParamsToStr(params);
	return `/transactions/${depositAddress}${query === "" ? "" : `?${query}`}`;
}
